import React, { useState, useRef, useEffect } from 'react';
import { CONTACT_INFO, getWhatsAppURL } from '../config/contactConfig';

// ─── WhatsApp SVG Icon ────────────────────────────────────────────────────────
const WhatsAppIcon = ({ size = 28, color = 'white' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

// ─── Booking Form ─────────────────────────────────────────────────────────────
const OCCASIONS = [
  'Wedding', 'Birthday Party', 'Corporate Event',
  'Anniversary', 'Baby Shower', 'Engagement', 'Other',
];

const INITIAL_FORM = {
  name: '', contact: '', occasion: '', date: '', time: '', notes: '',
};

const BookingForm = ({ onClose }) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const set = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.name.trim())    e.name     = 'Required';
    if (!form.contact.trim()) e.contact  = 'Required';
    if (!form.occasion)       e.occasion = 'Required';
    if (!form.date)           e.date     = 'Required';
    if (!form.time)           e.time     = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    setSubmitting(true);

    const formattedDate = new Date(form.date).toLocaleDateString('en-IN', {
      day: 'numeric', month: 'long', year: 'numeric',
    });
    const formattedTime = new Date(`1970-01-01T${form.time}`).toLocaleTimeString('en-IN', {
      hour: '2-digit', minute: '2-digit',
    });

    const message =
      `*New Booking Request* 🎉\n\n` +
      `*Name:* ${form.name}\n` +
      `*Contact:* ${form.contact}\n` +
      `*Occasion:* ${form.occasion}\n` +
      `*Date:* ${formattedDate}\n` +
      `*Time:* ${formattedTime}\n` +
      (form.notes ? `*Notes:* ${form.notes}\n` : '') +
      `\n_Sent via website booking form_`;

    window.open(getWhatsAppURL(message), '_blank');
    setSubmitting(false);
    onClose();
  };

  const inputCls =
    'w-full text-sm px-3 py-2 rounded-lg border bg-white focus:outline-none focus:border-green-400 transition-colors';
  const errCls = 'border-red-300 bg-red-50';
  const okCls  = 'border-gray-200';

  return (
    <div className="absolute bottom-20 right-0 w-80 bg-white rounded-2xl shadow-2xl overflow-hidden z-50">
      {/* Header */}
      <div className="bg-[#25D366] px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <WhatsAppIcon size={20} />
          <div>
            <p className="text-white font-semibold text-sm leading-none">Book via WhatsApp</p>
            <p className="text-green-100 text-xs mt-0.5">We'll confirm within minutes</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-white/80 hover:text-white text-xl leading-none transition-colors"
          aria-label="Close"
        >
          ×
        </button>
      </div>

      {/* Form Body */}
      <div className="p-4 space-y-3">
        {/* Name + Contact */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs text-gray-500 font-medium mb-1 block">Name *</label>
            <input
              type="text"
              placeholder="Your name"
              value={form.name}
              onChange={set('name')}
              className={`${inputCls} ${errors.name ? errCls : okCls}`}
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 font-medium mb-1 block">Contact *</label>
            <input
              type="tel"
              placeholder="+91 98765…"
              value={form.contact}
              onChange={set('contact')}
              className={`${inputCls} ${errors.contact ? errCls : okCls}`}
            />
          </div>
        </div>

        {/* Occasion */}
        <div>
          <label className="text-xs text-gray-500 font-medium mb-1 block">Occasion *</label>
          <select
            value={form.occasion}
            onChange={set('occasion')}
            className={`${inputCls} ${errors.occasion ? errCls : okCls} cursor-pointer`}
          >
            <option value="">Select occasion…</option>
            {OCCASIONS.map((o) => <option key={o}>{o}</option>)}
          </select>
        </div>

        {/* Date + Time */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs text-gray-500 font-medium mb-1 block">Date *</label>
            <input
              type="date"
              value={form.date}
              onChange={set('date')}
              min={new Date().toISOString().split('T')[0]}
              className={`${inputCls} ${errors.date ? errCls : okCls}`}
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 font-medium mb-1 block">Time *</label>
            <input
              type="time"
              value={form.time}
              onChange={set('time')}
              className={`${inputCls} ${errors.time ? errCls : okCls}`}
            />
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="text-xs text-gray-500 font-medium mb-1 block">Notes</label>
          <textarea
            rows={2}
            placeholder="Guest count, special requests…"
            value={form.notes}
            onChange={set('notes')}
            className={`${inputCls} ${okCls} resize-none`}
          />
        </div>

        {/* Error summary */}
        {Object.keys(errors).length > 0 && (
          <p className="text-xs text-red-500">Please fill in all required fields.</p>
        )}

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="w-full bg-[#25D366] hover:bg-[#1ebe5a] disabled:opacity-60 text-white font-semibold text-sm py-2.5 rounded-xl transition-colors flex items-center justify-center gap-2"
        >
          <WhatsAppIcon size={16} />
          {submitting ? 'Opening WhatsApp…' : 'Send Booking on WhatsApp'}
        </button>
      </div>
    </div>
  );
};

// ─── Quick Menu ───────────────────────────────────────────────────────────────
const QuickMenu = ({ onBook, onClose }) => {
  const items = [
    { icon: '📅', label: 'Book an Event',   action: onBook },
    { icon: '💬', label: 'General Inquiry', action: () => { window.open(getWhatsAppURL(CONTACT_INFO.WHATSAPP_MESSAGES.INQUIRY), '_blank'); onClose(); } },
    { icon: '🛠️', label: 'Support',          action: () => { window.open(getWhatsAppURL(CONTACT_INFO.WHATSAPP_MESSAGES.SUPPORT), '_blank'); onClose(); } },
  ];

  return (
    <div className="absolute bottom-20 right-0 bg-white rounded-2xl shadow-xl overflow-hidden w-52 z-50">
      <p className="text-xs font-semibold text-gray-400 px-4 py-2.5 border-b border-gray-100">
        How can we help?
      </p>
      {items.map(({ icon, label, action }) => (
        <button
          key={label}
          onClick={action}
          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-green-50 text-left text-sm text-gray-700 font-medium transition-colors"
        >
          <span className="text-base">{icon}</span>
          {label}
        </button>
      ))}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const FloatingWhatsAppButton = () => {
  const [view, setView] = useState(null); // null | 'menu' | 'form'
  const containerRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setView(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = () => setView((prev) => (prev ? null : 'menu'));

  return (
    <div ref={containerRef} className="fixed bottom-6 right-6 z-40 flex flex-col items-end">
      {view === 'menu' && (
        <QuickMenu onBook={() => setView('form')} onClose={() => setView(null)} />
      )}
      {view === 'form' && (
        <BookingForm onClose={() => setView(null)} />
      )}

      {/* FAB */}
      <div className="relative">
        {/* Pulse ring — only when closed */}
        {!view && (
          <span className="absolute inset-0 rounded-full animate-ping bg-[#25D366] opacity-30 pointer-events-none" />
        )}

        <button
          onClick={toggle}
          aria-label="Chat with us on WhatsApp"
          className="relative flex items-center justify-center w-16 h-16 rounded-full shadow-lg bg-[#25D366] hover:bg-[#1ebe5a] transition-all duration-200 hover:scale-110 active:scale-95 focus:outline-none focus-visible:ring-4 focus-visible:ring-green-300"
        >
          {view ? (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          ) : (
            <WhatsAppIcon size={30} />
          )}
        </button>
      </div>

      {!view && (
        <p className="mt-1 text-xs text-gray-500 bg-white px-2 py-0.5 rounded-full shadow text-center whitespace-nowrap">
          Chat with us
        </p>
      )}
    </div>
  );
};

export default FloatingWhatsAppButton;